import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import {
  ArrowLeft,
  TrendingUp,
  Calendar,
  /* Bell, BellOff, */ Trophy,
  ChevronDown,
} from "lucide-react";
//import { usePushNotifications } from '../hooks/usePushNotifications';
import PlayerStatsModal from "../components/PlayerStatsModal";
import {
  getTeamStyle,
  getTextColorForAccent,
  getReadableTeamColor,
  isDarkModeActive,
} from "../utils/teamColors";
import JerseyNumber from "../utils/JerseyNumber";

// ── Initials avatar ───────────────────────────────────────────────────────────
const TeamAvatar = ({ name, logoUrl, size = 56, textColor = "#ffffff" }) => {
  const initials = name
    ? name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "??";
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        style={{ width: size, height: size }}
        className="rounded-xl object-contain bg-white/20 p-1 flex-shrink-0"
      />
    );
  }
  const isLight = textColor === "#ffffff";
  return (
    <div
      style={{
        width: size,
        height: size,
        color: textColor,
        backgroundColor: isLight
          ? "rgba(255,255,255,0.20)"
          : "rgba(0,0,0,0.10)",
        borderColor: isLight ? "rgba(255,255,255,0.30)" : "rgba(0,0,0,0.20)",
      }}
      className="rounded-xl flex items-center justify-center
                       font-bold text-xl flex-shrink-0 border"
    >
      {initials}
    </div>
  );
};

// ── Stat chip ─────────────────────────────────────────────────────────────────
const StatChip = ({ value, label, accentHex }) => {
  // Use getTextColorForAccent to determine if accent is readable on light/dark chip bg
  // Light mode chip bg ≈ #f9fafb; dark mode chip bg ≈ #374151
  const lightBgContrast = getTextColorForAccent("#f9fafb", false);
  const darkBgContrast = getTextColorForAccent("#374151", true);

  // If accent itself is readable on the chip bg, use it; otherwise fall back to neutral
  const lightColor =
    getTextColorForAccent(accentHex || "#374151", false) !== lightBgContrast
      ? accentHex || "#374151"
      : "#374151";
  const darkColor =
    getTextColorForAccent(accentHex || "#e5e7eb", true) !== darkBgContrast
      ? accentHex || "#e5e7eb"
      : "#e5e7eb";

  return (
    <div className="bg-gray-50 dark:bg-gray-700/60 rounded-lg p-3 text-center">
      <div className="text-xl font-bold" style={{ color: lightColor }}>
        <span className="dark:hidden">{value ?? "—"}</span>
        <span className="hidden dark:inline" style={{ color: darkColor }}>
          {value ?? "—"}
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500 mt-1">
        {label}
      </div>
    </div>
  );
};

// ── W/L pill ──────────────────────────────────────────────────────────────────
const ResultPill = ({ result }) =>
  result === "W" ? (
    <span
      className="text-[11px] font-bold px-2 py-0.5 rounded-full
                         bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400"
    >
      W
    </span>
  ) : (
    <span
      className="text-[11px] font-bold px-2 py-0.5 rounded-full
                         bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400"
    >
      L
    </span>
  );

// ── Average from array of game_stats rows ────────────────────────────────────
const avg = (rows, key) => {
  if (!rows.length) return "—";
  const total = rows.reduce((sum, r) => sum + (r[key] ?? 0), 0);
  return (total / rows.length).toFixed(1);
};

// ── AllStarVotingCard component ────────────────────────────────────
const AllStarVotingCard = ({ votes, accentHex, loading }) => {
  // Component defaults to opened state (true)
  const [isOpen, setIsOpen] = useState(true);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 px-5 py-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={16} style={{ color: accentHex }} />
          <h2 className="font-bold text-gray-900 dark:text-white text-sm">
            All-Star Voting
          </h2>
        </div>
        <div className="space-y-3">
          {[80, 55, 40, 30].map((w) => (
            <div key={w} className="animate-pulse">
              <div
                className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full mb-1"
                style={{ width: "40%" }}
              />
              <div
                className="h-5 bg-gray-100 dark:bg-gray-700/60 rounded-full"
                style={{ width: `${w}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!votes || votes.length === 0) return null;

  const maxVotes = votes[0].votes; // already sorted desc
  const totalVotes = votes.reduce((s, v) => s + v.votes, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
      {/* Clickable Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-150 focus:outline-none"
      >
        <div className="flex items-center gap-2">
          <Trophy size={16} style={{ color: accentHex }} />
          <h2 className="font-bold text-gray-900 dark:text-white text-sm">
            All-Star Voting
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
          </span>
          {/* Animated Arrow Indicator */}
          <ChevronDown
            size={16}
            className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Collapsible Content Body */}
      {isOpen && (
        <div className="px-5 pb-5 pt-1 space-y-3 border-t border-gray-100 dark:border-gray-700/50">
          {votes.map(({ player, votes: count }, i) => {
            const pct = maxVotes > 0 ? (count / maxVotes) * 100 : 0;
            const isLeader = i === 0;

            return (
              <div key={player}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isLeader && (
                      <span
                        className="text-xs flex-shrink-0"
                        style={{ color: accentHex }}
                      >
                        ★
                      </span>
                    )}
                    <span
                      className={`text-sm truncate ${isLeader ? "font-semibold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}
                    >
                      {player}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-3 flex-shrink-0">
                    {count} vote{count !== 1 ? "s" : ""}
                  </span>
                </div>

                <div className="h-2 w-full bg-gray-100 dark:bg-gray-700/60 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${pct}%`,
                      background: isLeader ? accentHex : `${accentHex}70`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── NotifyButton sub-component ─────────────────────────────────────────────────
/*
const NotifyButton = ({
    teamName,
    isSupported,
    isIOS,
    isStandalone,
    isFollowing,
    isLoading,
    error,
    onToggle,
    team,
}) => {
    const { accentHex } = getTeamStyle(team);

    if (!isSupported && !isIOS) return null;

    if (isIOS && !isStandalone) {
        return (
            <div
                className="flex items-center gap-2 px-4 py-3 rounded-xl border"
                style={{ borderColor: `${accentHex}40`, backgroundColor: `${accentHex}12` }}
            >
                <Bell size={14} style={{ color: accentHex }} className="flex-shrink-0" />
                <p className="text-xs" style={{ color: accentHex }}>
                    Add this page to your Home Screen to get game notifications.
                </p>
            </div>
        );
    }

    if (error === 'permission-denied') {
        return (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl
                        bg-gray-100 dark:bg-gray-700/50
                        border border-gray-200 dark:border-gray-600">
                <BellOff size={14} className="text-gray-400 flex-shrink-0" />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Notifications blocked.{' '}
                    <span className="underline cursor-default">
                        Enable them in your browser settings to follow {teamName}.
                    </span>
                </p>
            </div>
        );
    }

    return (
        <button
            onClick={onToggle}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            style={isFollowing
                ? { backgroundColor: `${accentHex}18`, borderColor: `${accentHex}60`, color: accentHex }
                : { backgroundColor: 'transparent', borderColor: '#d1d5db', color: '#374151' }
            }
        >
            {isLoading ? (
                <>
                    <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span>Saving…</span>
                </>
            ) : isFollowing ? (
                <>
                    <Bell size={15} className="fill-current" />
                    <span>Notified · Tap to turn off</span>
                </>
            ) : (
                <>
                    <Bell size={15} />
                    <span>Notify me about {teamName} games</span>
                </>
            )}
        </button>
    );
};
*/

// ── Main component ────────────────────────────────────────────────────────────
const TeamProfile = () => {
  const { id } = useParams();
  const teamId = parseInt(id, 10);
  const navigate = useNavigate();
  const { activeSeason } = useSeason();

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]); // [{ id, name, stats: [...] }]
  const [games, setGames] = useState([]); // finished games this team played
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [standing, setStanding] = useState(null);

  // Modal state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerGameStats, setPlayerGameStats] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const [allstarVotes, setAllstarVotes] = useState([]);
  const [allstarLoading, setAllstarLoading] = useState(false);
  const [allstarEnabled, setAllstarEnabled] = useState(false); //To remove all-star when not needed

  /*
    const {
        isSupported,
        isIOS,
        isStandalone,
        subscribedTeamIds,
        isLoading: notifLoading,
        error: notifError,
        subscribe,
        unsubscribe,
    } = usePushNotifications();

    const isFollowingThisTeam = subscribedTeamIds.some(
        (id) => String(id) === String(teamId)
    );

    const handleNotifyToggle = () => {
        if (isFollowingThisTeam) {
            unsubscribe(teamId);
        } else {
            subscribe(teamId);
        }
    };

    */
  const fetchAllstarVotes = useCallback(async (teamName) => {
    setAllstarLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "get-allstar-votes",
        {
          body: { teamName },
        },
      );
      if (error) throw error;
      setAllstarVotes(data?.votes ?? []);
    } catch (err) {
      console.error("All-star votes fetch error:", err);
      // Non-fatal — voting card just won't render
      setAllstarVotes([]);
    } finally {
      setAllstarLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!activeSeason) return;
    setLoading(true);
    setError(null);

    try {
      // 1. Team info from View
      const { data: teamData, error: teamErr } = await supabase
        .from("team_profiles")
        .select(
          "id, name, logo_url, captain_id, captain_name, primary_color, secondary_color, gradient",
        )
        .eq("id", teamId)
        .single();

      if (teamErr) throw teamErr;
      setTeam(teamData);

      const { data: settings } = await supabase
        .from("league_settings")
        .select("allstar_enabled")
        .eq("season_id", activeSeason.id)
        .maybeSingle();
      const enabled = settings?.allstar_enabled === true;
      setAllstarEnabled(enabled);
      if (enabled) fetchAllstarVotes(teamData.name);

      // 1b. Standing from view (gives us GB, win_pct, rank — no re-derivation needed)
      const { data: standingRow, error: standingErr } = await supabase
        .from("standings")
        .select("rank, wins, losses, win_pct, point_diff, games_behind, streak")
        .eq("id", teamId)
        .eq("season_id", activeSeason.id)
        .maybeSingle();
      // Non-fatal — fall back gracefully if view row missing
      if (!standingErr) setStanding(standingRow);

      // 2. Roster + per-player season stats
      //    Fetch all players on this team, then their game_stats for this season
      const { data: playerRows, error: playerErr } = await supabase
        .from("players")
        .select(
          `
                    id,
                    name,
                    jersey_number,
                    game_stats (
                        points, rebounds, assists,
                        three_pm, two_pm, ft_made,
                        personal_fouls, technical_fouls,
                        game:games!game_stats_game_id_fkey (
                            season_id, status
                        )
                    )
                `,
        )
        .eq("team_id", teamId);
      if (playerErr) throw playerErr;

      // Filter stats to this season's completed games only, then compute per-game avgs
      const enriched = (playerRows || []).map((p) => {
        const seasonStats = (p.game_stats || []).filter((s) => {
          if (
            !s.game ||
            s.game.season_id !== activeSeason.id ||
            s.game.status !== "finished"
          ) {
            return false;
          }
          // ── DNP Filter: Exclude games where all stats are zero ──
          const isDNP =
            s.points === 0 &&
            s.rebounds === 0 &&
            s.assists === 0 &&
            s.three_pm === 0 &&
            s.two_pm === 0 &&
            s.ft_made === 0 &&
            (s.personal_fouls ?? 0) === 0 &&
            (s.technical_fouls ?? 0) === 0;
          return !isDNP;
        });
        return {
          ...p,
          seasonStats,
          team_primary_color: teamData?.primary_color,
          team_secondary_color: teamData?.secondary_color,
          team_gradient: teamData?.gradient,
        };
      });
      // Sort by total points desc
      enriched.sort((a, b) => {
        const apts = a.seasonStats.reduce((s, r) => s + (r.points ?? 0), 0);
        const bpts = b.seasonStats.reduce((s, r) => s + (r.points ?? 0), 0);
        return bpts - apts;
      });
      setPlayers(enriched);

      // 3. Game log — all final games for this team this season
      const { data: gameRows, error: gamesErr } = await supabase
        .from("games")
        .select(
          `
                    id, game_date, status, court, game_type,
                    team1_id, team2_id, team1_score, team2_score,
                    team1:teams!games_team1_id_fkey(id, name),
                    team2:teams!games_team2_id_fkey(id, name)
                `,
        )
        .eq("season_id", activeSeason.id)
        .eq("status", "finished")
        .or(`team1_id.eq.${teamId},team2_id.eq.${teamId}`)
        .order("game_date", { ascending: false });
      if (gamesErr) throw gamesErr;
      setGames(gameRows || []);
    } catch (err) {
      console.error("TeamProfile fetch error:", err);
      setError("Failed to load team data.");
    } finally {
      setLoading(false);
    }
  }, [teamId, activeSeason, fetchAllstarVotes]);

  // Fetch player's game-by-game stats for modal
  const loadPlayerGameStats = useCallback(async (playerId, seasonId) => {
    setModalLoading(true);
    const { data, error } = await supabase
      .from("game_stats")
      .select(
        `
                points, rebounds, assists, personal_fouls, technical_fouls,
                three_pm, two_pm, ft_made,
                game:games!game_stats_game_id_fkey(
                    id, game_date, game_type, playoff_round, team1_id, team2_id, team1_score, team2_score,
                    team1:teams!games_team1_id_fkey(id, name),
                    team2:teams!games_team2_id_fkey(id, name)
                )
            `,
      )
      .eq("player_id", playerId)
      .eq("game.season_id", seasonId);

    if (!error && data) {
      setPlayerGameStats(data);
    }
    setModalLoading(false);
  }, []);

  const handlePlayerClick = (player) => {
    setSelectedPlayer({
      ...player,
      jersey: player.jersey_number,
      team: team?.name,
      team_id: teamId,
    });
    loadPlayerGameStats(player.id, activeSeason.id);
  };
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const allSeasonStats = players.flatMap((p) => p.seasonStats);
  // Per-game team totals (sum per game, then average across games)
  const teamPPG = (() => {
    if (!games.length) return "—";
    const total = games.reduce((sum, g) => {
      return sum + (g.team1_id === teamId ? g.team1_score : g.team2_score);
    }, 0);
    return (total / games.length).toFixed(1);
  })();

  // ── Render helpers ─────────────────────────────────────────────────────────
  const gameResult = (game) => {
    const isTeam1 = game.team1_id === teamId;
    const myScore = isTeam1 ? game.team1_score : game.team2_score;
    const oppScore = isTeam1 ? game.team2_score : game.team1_score;
    return myScore > oppScore ? "W" : "L";
  };

  const gameOpponent = (game) => {
    const isTeam1 = game.team1_id === teamId;
    const opp = isTeam1 ? game.team2 : game.team1;
    const prefix = "vs"; // use { const prefix = isTeam1 ? 'vs' : '@' } for home/away feature
    return `${prefix} ${opp?.name ?? "Unknown"}`;
  };

  const gameScore = (game) => {
    const isTeam1 = game.team1_id === teamId;
    const my = isTeam1 ? game.team1_score : game.team2_score;
    const opp = isTeam1 ? game.team2_score : game.team1_score;
    return `${my} – ${opp}`;
  };

  // ── Loading / error states ─────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-gray-400 dark:text-gray-500 text-sm animate-pulse">
          Loading team...
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="text-center py-24">
        <p className="text-red-500 dark:text-red-400 text-sm mb-4">
          {error ?? "Team not found."}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-orange-500 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Back link ── */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400
                           hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <ArrowLeft size={15} />
        Back
      </button>

      {/* ── Team header banner ── */}
      {(() => {
        const ts = getTeamStyle(team);
        const headerTextColor = getTextColorForAccent(
          ts.primaryHex,
          isDarkModeActive(),
        );
        const isLightText = headerTextColor === "#ffffff";
        const subTextColor = isLightText
          ? "rgba(255,255,255,0.65)"
          : "rgba(0,0,0,0.55)";
        const borderColor = isLightText
          ? "rgba(255,255,255,0.20)"
          : "rgba(0,0,0,0.12)";
        const labelColor = isLightText
          ? "rgba(255,255,255,0.55)"
          : "rgba(0,0,0,0.45)";
        return (
          <div
            className="rounded-2xl p-6"
            style={{ ...ts.headerStyle, color: headerTextColor }}
          >
            <div className="flex items-center gap-4 mb-5">
              <TeamAvatar
                name={team.name}
                logoUrl={team.logo_url}
                textColor={headerTextColor}
              />
              <div>
                <h1
                  className="text-2xl font-black tracking-tight"
                  style={{ color: headerTextColor }}
                >
                  {team.name}
                </h1>
                <p className="text-sm mt-0.5" style={{ color: subTextColor }}>
                  Captain: {team.captain_name ?? "Unknown"} ·{" "}
                  {activeSeason?.name ?? ""}
                </p>
              </div>
            </div>

            {/* Record strip — from standings view */}
            <div
              className="grid grid-cols-4 gap-4 pt-4"
              style={{ borderTop: `1px solid ${borderColor}` }}
            >
              {[
                {
                  n: standing ? `${standing.wins}–${standing.losses}` : "—",
                  l: "Record",
                },
                {
                  n:
                    (standing?.point_diff ?? 0) > 0
                      ? `+${standing.point_diff}`
                      : (standing?.point_diff ?? "—"),
                  l: "Pt Diff",
                },
                {
                  n:
                    standing?.games_behind === 0 ||
                    standing?.games_behind == null
                      ? "—"
                      : standing.games_behind,
                  l: "Games Behind",
                },
                {
                  n:
                    standing?.win_pct != null
                      ? `${parseFloat(standing.win_pct).toFixed(0)}%`
                      : "—",
                  l: "Win %",
                },
              ].map(({ n, l }) => (
                <div key={l} className="text-center">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: headerTextColor }}
                  >
                    {n}
                  </div>
                  <div
                    className="text-[10px] uppercase tracking-widest mt-0.5"
                    style={{ color: labelColor }}
                  >
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── Notifications ── */}
      {/*<NotifyButton
                teamName={team.name}
                isSupported={isSupported}
                isIOS={isIOS}
                isStandalone={isStandalone}
                isFollowing={isFollowingThisTeam}
                isLoading={notifLoading}
                error={notifError}
                onToggle={handleNotifyToggle}
                team={team}
            />*/}

      {/* ── Team avg stat chips ── */}
      <div className="grid grid-cols-4 gap-3">
        <StatChip
          value={teamPPG}
          label="PPG"
          accentHex={getTeamStyle(team).accentHex}
        />
        <StatChip
          value={avg(allSeasonStats, "rebounds")}
          label="RPG"
          accentHex={getTeamStyle(team).accentHex}
        />
        <StatChip
          value={avg(allSeasonStats, "assists")}
          label="APG"
          accentHex={getTeamStyle(team).accentHex}
        />
      </div>

      {allstarEnabled && (
        <AllStarVotingCard
          votes={allstarVotes}
          accentHex={getReadableTeamColor(
            team,
            isDarkModeActive() ? "#1f2937" : "#ffffff",
            isDarkModeActive(),
          )}
          loading={allstarLoading}
        />
      )}

      {/* ── Roster / season stats table ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <TrendingUp
            size={16}
            style={{ color: getTeamStyle(team).accentHex }}
          />
          <h2 className="font-bold text-gray-900 dark:text-white text-sm">
            Roster · Season Stats
          </h2>
        </div>

        {players.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
            No player data yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  <th className="text-left px-5 py-3 font-medium">Player</th>
                  <th className="text-right px-3 py-3 font-medium">GP</th>
                  <th className="text-right px-3 py-3 font-medium">PTS</th>
                  <th className="text-right px-3 py-3 font-medium">REB</th>
                  <th className="text-right px-3 py-3 font-medium">AST</th>
                  <th className="text-right px-5 py-3 font-medium">3PM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/60">
                {players.map((p, i) => {
                  const gp = p.seasonStats.length;
                  const pts = gp
                    ? (
                        p.seasonStats.reduce((s, r) => s + (r.points ?? 0), 0) /
                        gp
                      ).toFixed(1)
                    : "—";
                  const reb = gp
                    ? (
                        p.seasonStats.reduce(
                          (s, r) => s + (r.rebounds ?? 0),
                          0,
                        ) / gp
                      ).toFixed(1)
                    : "—";
                  const ast = gp
                    ? (
                        p.seasonStats.reduce(
                          (s, r) => s + (r.assists ?? 0),
                          0,
                        ) / gp
                      ).toFixed(1)
                    : "—";
                  const threes = gp
                    ? p.seasonStats.reduce((s, r) => s + (r.three_pm ?? 0), 0)
                    : "—";

                  const ts = getTeamStyle(team);
                  const accentTextColor = getTextColorForAccent(
                    ts.accentHex,
                    isDarkModeActive(),
                  );
                  return (
                    <tr
                      key={p.id}
                      onClick={() => handlePlayerClick(p)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center gap-3">
                          <JerseyNumber
                            number={p.jersey_number}
                            color={ts.accentHex}
                            size="xs"
                            mode="badge"
                          />
                          <span>{p.name}</span>
                          {p.name === team?.captain_name && (
                            <span
                              className="ml-2 text-[9px] px-1.5 py-0.5 rounded font-bold border"
                              style={{
                                ...ts.accentStyle,
                                borderColor: ts.accentHex,
                                color: accentTextColor,
                              }}
                            >
                              C
                            </span>
                          )}
                          {i === 0 && (
                            <span
                              className="text-xs"
                              style={{ color: ts.accentHex }}
                              title="Team Leader"
                            >
                              ★
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-500 dark:text-gray-400">
                        {gp}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold text-gray-900 dark:text-white">
                        {pts}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 dark:text-gray-300">
                        {reb}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 dark:text-gray-300">
                        {ast}
                      </td>
                      <td className="px-5 py-3 text-right text-gray-600 dark:text-gray-300">
                        {threes}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Game log ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Calendar size={16} style={{ color: getTeamStyle(team).accentHex }} />
          <h2 className="font-bold text-gray-900 dark:text-white text-sm">
            Game Log
          </h2>
        </div>

        {games.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
            No completed games yet.
          </p>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/60">
            {games.map((game) => (
              <button
                key={game.id}
                onClick={() => navigate(`/game/${game.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3.5
                                           hover:bg-gray-50 dark:hover:bg-gray-700/30
                                           transition-colors text-left group"
              >
                {/* Date */}
                <span className="text-xs text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">
                  {new Date(game.game_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>

                {/* Opponent */}
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 flex items-center gap-2">
                  {gameOpponent(game)}
                  {game.game_type === "playoff" && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                      Playoff
                    </span>
                  )}
                </span>

                {/* Court badge */}
                {game.court && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 hidden sm:block">
                    {game.court}
                  </span>
                )}

                {/* Result pill */}
                <ResultPill result={gameResult(game)} />

                {/* Score */}
                <span className="text-sm font-semibold text-gray-900 dark:text-white w-16 text-right">
                  {gameScore(game)}
                </span>

                {/* Arrow hint */}
                <span
                  className="transition-colors text-xs ml-1 opacity-40 group-hover:opacity-100"
                  style={{ color: getTeamStyle(team).accentHex }}
                >
                  →
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Player Stats Modal ── */}
      {selectedPlayer && (
        <PlayerStatsModal
          player={selectedPlayer}
          gameStats={playerGameStats}
          modalLoading={modalLoading}
          teamStyle={getTeamStyle({
            primary_color: selectedPlayer.team_primary_color,
            secondary_color: selectedPlayer.team_secondary_color,
            gradient: selectedPlayer.team_gradient,
          })}
          onClose={() => {
            setSelectedPlayer(null);
            setPlayerGameStats([]);
          }}
        />
      )}
    </div>
  );
};

export default TeamProfile;
